import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { ENABLE_AUTO_MOBILE_OTP_VERIFICATION } from 'constants/featureFlag';
import { noop } from 'helpers/utils';

interface AutoMobileVerificationStateType {
  hashCode: string;
  otp: string;
}

interface AutoMobileVerificationContextType extends AutoMobileVerificationStateType {
  setHashCode: (code: string) => void;
  setOTP: (otp: string) => void;
  resetData: () => void;
}

const DEFAULT_STATE: AutoMobileVerificationStateType = {
  hashCode: '',
  otp: '',
};

const DEFAULT_CONTEXT_VALUE: AutoMobileVerificationContextType = {
  ...DEFAULT_STATE,
  setOTP: noop,
  setHashCode: noop,
  resetData: noop,
};

const AutoMobileVerificationContext =
  createContext<AutoMobileVerificationContextType>(DEFAULT_CONTEXT_VALUE);

const useAutoMobileOTPVerification = () => {
  const context = useContext(AutoMobileVerificationContext);
  if (!context) return DEFAULT_CONTEXT_VALUE;
  return context;
};

const AutoMobileOTPVerificationProvider = ({ children }) => {
  const [state, setState] = useState<AutoMobileVerificationStateType>(DEFAULT_STATE);

  const setHashCode = useCallback((code: string = '') => {
    if (ENABLE_AUTO_MOBILE_OTP_VERIFICATION) {
      setState(current => ({ ...current, hashCode: code }));
    }
  }, []);

  const setOTP = useCallback((otp: string = '') => {
    if (ENABLE_AUTO_MOBILE_OTP_VERIFICATION) {
      setState(current => ({ ...current, otp }));
    }
  }, []);

  const resetData = useCallback(() => {
    setState({ ...DEFAULT_STATE });
  }, []);

  const defaultProviderValues = useMemo(
    () => ({ ...state, setHashCode, setOTP, resetData }),
    [state, resetData, setHashCode, setOTP]
  );

  return (
    <AutoMobileVerificationContext.Provider value={defaultProviderValues}>
      {children}
    </AutoMobileVerificationContext.Provider>
  );
};

const AutoMobileOTPVerificationInitializer = ({ children }) => {
  if (!ENABLE_AUTO_MOBILE_OTP_VERIFICATION) return children;

  return (
    <AutoMobileOTPVerificationProvider>{children}</AutoMobileOTPVerificationProvider>
  );
};

export { useAutoMobileOTPVerification };
export default AutoMobileOTPVerificationInitializer;
